import { useState } from 'react';

import WidgetSettingsModal from '@/features/dashboard/components/WidgetSettingsModal';
import { cn } from '@/lib/cn';

import AppIcon from './AppIcon';
import AppLinkModal from './AppLinkModal';
import type { LauncherApp } from './catalog';
import { isPlainLeftClick } from './linkClick';
import useAppLauncherConfig from './useAppLauncherConfig';

const EllipsisIcon = (
  <svg viewBox="0 0 20 20" fill="currentColor" aria-hidden="true" className="h-5 w-5">
    <path d="M6 10a1.25 1.25 0 11-2.5 0 1.25 1.25 0 012.5 0zm5 0a1.25 1.25 0 11-2.5 0 1.25 1.25 0 012.5 0zm3.75 1.25a1.25 1.25 0 100-2.5 1.25 1.25 0 000 2.5z" />
  </svg>
);

const focusRing =
  'transition group-hover:-translate-y-0.5 group-focus-visible:ring-2 group-focus-visible:ring-(--accent-primary) group-focus-visible:ring-offset-2 group-focus-visible:ring-offset-(--bg-app-solid)';

/**
 * The app dock: floating app icons with no card shell. Icons use their bundled
 * graphic or a color+initial fallback. The ⋯ button opens the enable/disable
 * settings (custom-app CRUD is a separate refinement).
 */
export default function AppLauncherWidget() {
  const apps = useAppLauncherConfig();
  const [selectedApp, setSelectedApp] = useState<LauncherApp | null>(null);
  const [settingsOpen, setSettingsOpen] = useState(false);

  return (
    <nav aria-label="App launcher" className="flex flex-wrap items-start gap-x-5 gap-y-3">
      {apps.map((app) => (
        <a
          key={app.id}
          href={app.url}
          target="_blank"
          rel="noopener noreferrer"
          title={app.name}
          aria-label={app.name}
          aria-haspopup="dialog"
          onClick={(e) => {
            // Leave modified / middle / right clicks to the browser so native
            // "open in new tab/window" and the context menu keep working.
            if (!isPlainLeftClick(e)) return;
            e.preventDefault();
            setSelectedApp(app);
          }}
          className="group flex w-16 flex-col items-center gap-1.5 focus-visible:outline-none"
        >
          <AppIcon app={app} className={focusRing} />
          <span className="max-w-full truncate text-xs text-(--text-tertiary)">{app.name}</span>
        </a>
      ))}

      <button
        type="button"
        onClick={() => setSettingsOpen(true)}
        aria-label="Edit apps"
        className="group flex w-16 flex-col items-center gap-1.5 focus-visible:outline-none"
      >
        <span
          className={cn(
            'flex h-14 w-14 items-center justify-center rounded-2xl border border-dashed border-(--border-strong) text-(--text-tertiary)',
            'group-hover:border-(--border-default) group-hover:text-(--text-secondary)',
            focusRing,
          )}
        >
          {EllipsisIcon}
        </span>
        <span className="text-xs text-(--text-muted)">edit</span>
      </button>

      <AppLinkModal app={selectedApp} onClose={() => setSelectedApp(null)} />
      <WidgetSettingsModal widgetId="app-launcher" open={settingsOpen} onClose={() => setSettingsOpen(false)} />
    </nav>
  );
}
